export interface KiCodePrompt {
  id: number;
  text: string;
}

/**
 * Delivers a session's initial prompt once the session is ready. A prompt
 * counts as delivered only when `send` resolves true; a refused (false) or
 * rejected send leaves it pending until the session is ready again or sending
 * becomes available. A newer prompt replaces an older undelivered one.
 */
export const createPromptDelivery = (
  send: (text: string) => Promise<boolean>
) => {
  let ready = false;
  let deliveredId = 0;
  let pending: KiCodePrompt | null = null;
  let inFlight: KiCodePrompt | null = null;
  // A wake-up signal that arrived while a send was in flight, so a refused
  // send is attempted once more instead of losing the signal.
  let retryRequested = false;

  const attempt = () => {
    if (!ready || !pending || inFlight) return;
    const prompt = pending;
    inFlight = prompt;
    let result: Promise<boolean>;
    try {
      result = Promise.resolve(send(prompt.text));
    } catch {
      result = Promise.resolve(false);
    }
    void result
      .catch(() => false)
      .then((sent) => {
        inFlight = null;
        const retry = retryRequested;
        retryRequested = false;
        if (sent) {
          deliveredId = Math.max(deliveredId, prompt.id);
          if (pending?.id === prompt.id) pending = null;
        }
        if (pending && (retry || pending.id !== prompt.id)) attempt();
      });
  };

  const request = (prompt: KiCodePrompt | null) => {
    if (!prompt || prompt.id <= deliveredId) return;
    if (pending && prompt.id < pending.id) return;
    pending = prompt;
    attempt();
  };

  const wake = () => {
    if (inFlight) retryRequested = true;
    else attempt();
  };

  const onSessionReady = () => {
    ready = true;
    wake();
  };

  const onSendAvailable = wake;

  return { request, onSessionReady, onSendAvailable };
};
